import { getLocalizedValue } from "@repo/shared";
/**
 * Product-preference section of the sustainability widget (QL-142) — the
 * per-theme toggles plus, when the collection offers it, a visually separated
 * "No preference" row. Layout follows the Paper design linked from the ticket.
 *
 * Two display modes, picked per collection (`productPreferencesDisplay`):
 *   - `checkbox` — a two-column (column-first) list of checkbox rows, icon
 *     after the label; "No preference" as a muted checkbox row below a
 *     full-width divider;
 *   - `card` — the shared ChoiceCards tiles in `multiple` mode, with
 *     "No preference" as a dashed muted tile in its own row below them.
 *
 * Selection is mutually exclusive in both directions and moves silently:
 * picking "No preference" clears every theme; picking a theme while it is
 * active un-picks it. Themes stay multi-select among themselves. Never a
 * dialog, never an inline error. The label is a fixed UI translation.
 */

import type {
  SustainabilityProductPreferenceCriterion,
  SustainabilityProductPreferencesDisplay,
} from "@repo/shared/types";

import { Checkbox } from "@/components/ui/checkbox";
import { ChoiceCards } from "@/components/ui/choice-cards";
import { InlineSvg } from "@/components/ui/inline-svg";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./product-preferences-picker.i18n";

const HEADER_ID = "sustainability-pref-product-preferences-label";
const NO_PREFERENCE_ID = "sustainability-pref-no-preference";

export type ProductPreferencesValue = {
  productPreferences: Record<string, boolean>;
  noProductPreference: boolean;
};

export type ProductPreferencesPickerProps = {
  preferences: SustainabilityProductPreferenceCriterion[];
  /** Render the "No preference" row (= the shared rule's `required`). */
  noPreferenceEnabled: boolean;
  /** Checkbox list or icon-card grid (= `effectiveProductPreferencesDisplay`). */
  display: SustainabilityProductPreferencesDisplay;
  value: ProductPreferencesValue;
  onChange: (next: ProductPreferencesValue) => void;
  title: string;
  description: string;
  language: string;
  disabled?: boolean;
};

function allFalse(preferences: SustainabilityProductPreferenceCriterion[]) {
  return Object.fromEntries(preferences.map((pref) => [pref.key, false]));
}

type PreferenceCheckboxRowProps = {
  id: string;
  label: string;
  iconSvg?: string;
  checked: boolean;
  disabled: boolean;
  /** The muted "No preference" variant. */
  muted?: boolean;
  onCheckedChange: (checked: boolean) => void;
};

/** One 36px checkbox row of the checkbox list (theme or "No preference"). */
function PreferenceCheckboxRow({
  id,
  label,
  iconSvg,
  checked,
  disabled,
  muted,
  onCheckedChange,
}: PreferenceCheckboxRowProps) {
  return (
    <div className="flex min-h-9 items-center gap-2.5">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        disabled={disabled}
        className="h-5 w-5 rounded-md border-[1.5px] border-muted-foreground shadow-none data-[state=checked]:border-primary"
      />
      <Label
        htmlFor={id}
        className={cn(
          "inline-flex cursor-pointer items-center gap-2.5 text-sm font-normal",
          muted && "text-muted-foreground",
        )}
      >
        {label}
        {iconSvg && <InlineSvg svg={iconSvg} className="[&_svg]:h-[18px] [&_svg]:w-[18px]" />}
      </Label>
    </div>
  );
}

export function ProductPreferencesPicker({
  preferences,
  noPreferenceEnabled,
  display,
  value,
  onChange,
  title,
  description,
  language,
  disabled = false,
}: ProductPreferencesPickerProps) {
  const { tenantDefaultLanguage } = useUserLanguage();
  const t = useT(T);

  const toggleTheme = (key: string, checked: boolean) =>
    onChange({
      productPreferences: { ...value.productPreferences, [key]: checked },
      // A concrete theme un-picks "No preference".
      noProductPreference: checked ? false : value.noProductPreference,
    });

  const toggleNoPreference = (checked: boolean) =>
    onChange({ productPreferences: allFalse(preferences), noProductPreference: checked });

  const prefLabel = (pref: SustainabilityProductPreferenceCriterion) =>
    getLocalizedValue(pref.labelI18n, language, tenantDefaultLanguage) || pref.key;

  const header = (
    <div>
      <Label id={HEADER_ID} className="text-base">
        {title}
      </Label>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );

  if (display === "card") {
    return (
      <div className="space-y-3">
        {header}
        <ChoiceCards
          selection="multiple"
          aria-labelledby={HEADER_ID}
          options={preferences.map((pref) => ({
            id: pref.key,
            label: prefLabel(pref),
            iconSvg: pref.iconSvg,
          }))}
          value={preferences.filter((pref) => value.productPreferences[pref.key]).map((p) => p.key)}
          // Only *picking* a theme un-picks "No preference" — clearing the last
          // theme leaves it alone (it can't be on at that point anyway).
          onValueChange={(next, toggled) =>
            onChange({
              productPreferences: Object.fromEntries(
                preferences.map((pref) => [pref.key, next.includes(pref.key)]),
              ),
              noProductPreference: next.includes(toggled) ? false : value.noProductPreference,
            })
          }
          disabled={disabled}
          // Themes are a mixed bag — some carry an icon, some don't. Let the
          // icon-less tiles collapse to a centred label rather than reserving an
          // empty 32px slot, per the ticket's Paper board.
          reserveIconSlot={false}
        />
        {noPreferenceEnabled && (
          // Its own group on its own row: an opt-out, not a ninth theme.
          <ChoiceCards
            selection="multiple"
            aria-label={t.noPreference}
            options={[{ id: NO_PREFERENCE_ID, label: t.noPreference, variant: "muted" }]}
            value={value.noProductPreference ? [NO_PREFERENCE_ID] : []}
            onValueChange={(next) => toggleNoPreference(next.length > 0)}
            disabled={disabled}
          />
        )}
      </div>
    );
  }

  // Column-first split: first half left, second half right (per design).
  const half = Math.ceil(preferences.length / 2);
  const columns = [preferences.slice(0, half), preferences.slice(half)].filter(
    (col) => col.length > 0,
  );

  const renderRow = (pref: SustainabilityProductPreferenceCriterion) => (
    <PreferenceCheckboxRow
      key={pref.key}
      id={`sustainability-pref-product-preference-${pref.key}`}
      label={prefLabel(pref)}
      iconSvg={pref.iconSvg}
      checked={value.productPreferences[pref.key] === true}
      disabled={disabled}
      onCheckedChange={(checked) => toggleTheme(pref.key, checked)}
    />
  );

  return (
    <div className="space-y-2">
      {header}
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
        {columns.map((col, i) => (
          <div key={col[0]?.key ?? i} className="flex flex-col gap-0.5">
            {col.map(renderRow)}
          </div>
        ))}
      </div>
      {noPreferenceEnabled && (
        <>
          <Separator />
          <PreferenceCheckboxRow
            id={NO_PREFERENCE_ID}
            label={t.noPreference}
            checked={value.noProductPreference}
            disabled={disabled}
            muted
            onCheckedChange={toggleNoPreference}
          />
        </>
      )}
    </div>
  );
}
